//jobService

import apiService from "@/lib/api";
import { formatDateWithUserTimeZone } from "@/utils/dateUtils";
import { parseExperienceValue } from "@/utils/experience";
import { to24HourTime } from "@/utils/shiftTime";

// Shared by manualJobPost/cloneJob/updateJob — the three payload builders
// used to each hand-write `shift_timing: formData.shiftTiming || ""`
// (identical free-text string). Now that shift selection is structured
// (predefined shift id, or a job-specific custom start/end/timezone), one
// function derives the wire shape so Create/Edit/Clone can't drift.
//
// Three shapes, and the difference between the last two matters:
//   "predefined" / "custom" — the structured selection.
//   "none"                  — the user picked "No shift". Sends explicit
//                             nulls, which is how the server is told to
//                             clear a shift, legacy free text included.
//   blank                   — the picker was never touched. Sends no shift
//                             key at all, so the server leaves whatever the
//                             job already holds alone. Sending a null here
//                             is what erased a pre-existing job's shift on
//                             every unrelated edit.
const buildShiftPayload = (formData) => {
  if (formData.shiftType === "predefined" && formData.predefinedShiftId) {
    return { shift_type: "predefined", predefined_shift_id: formData.predefinedShiftId };
  }
  if (formData.shiftType === "custom") {
    return {
      shift_type: "custom",
      shift_start_time: to24HourTime(formData.shiftStartTime),
      shift_end_time: to24HourTime(formData.shiftEndTime),
      shift_timezone: formData.shiftTimezone || "",
    };
  }
  if (formData.shiftType === "none") {
    return { shift_type: null, shift_timing: null };
  }
  return {};
};

// Three states, same contract as buildShiftPayload:
//   key absent from the form  — omit it. The server leaves the job's existing
//                               calendar alone (AIModal/Step3.jsx builds its
//                               defaults without this field).
//   ""                        — explicit `calendar_type_id: null`, clearing it.
//   a real id                 — attaches that calendar.
// Keyed off the value, not react-hook-form's dirty-state: on Clone the
// prefilled calendar is the form's default, so nothing is dirty and a
// dirty-state check dropped the source job's calendar.
const buildCalendarTypePayload = (formData) => {
  if (formData.calendarTypeId === undefined) return {};
  return { calendar_type_id: formData.calendarTypeId || null };
};

// Shared by manualJobPost/cloneJob/updateJob, same story as buildShiftPayload:
// the wire shape is [{ language, proficiency | null }]. Proficiency is
// OPTIONAL — a job may demand a language without naming a level, and the
// server stores it with proficiency null (normalize_languages in
// app/api/v2/job/utils.py keeps such rows). Only the language is required;
// empty-proficiency rows travel as null so matching keys off presence.
const buildLanguagesRequiredPayload = (formData) =>
  (formData.languagesRequired || [])
    .map((row) => ({
      language: String(row?.language ?? "").trim(),
      proficiency:
        row?.proficiency == null || String(row.proficiency).trim() === ""
          ? null
          : String(row.proficiency).trim(),
    }))
    .filter((row) => row.language);

// The three job-level email-consent flows. Same three states as the two
// builders above, and the same reason for the middle one:
//   a real boolean — the user answered. `false` is an answer, not an unset:
//                    there is no Inherit state, so off has to reach the wire.
//   undefined      — the form drew no row for that flow (its module is
//                    withheld) or never resolved its defaults. JSON.stringify
//                    drops the key and the job keeps whatever it already holds.
// Never `null`. The server's module gate reads `is not None`, so an explicit
// null is a *selection* as far as it is concerned, and an unentitled tenant
// would be refused for a row it was never shown.
//
// snake_case, deliberately: JobCreateRequest does not forbid extra keys, so a
// camelCase name here would be accepted with a 200 and silently ignored.
const buildEmailConsentPayload = (formData) => ({
  require_apply_email_consent: formData.requireApplyEmailConsent ?? undefined,
  require_bulk_email_consent: formData.requireBulkEmailConsent ?? undefined,
  require_reupload_email_consent: formData.requireReuploadEmailConsent ?? undefined,
  require_jobfit_apply_email_consent: formData.requireJobfitApplyEmailConsent ?? undefined,
});

// Manual screening questions → wire shape. Sent by create, clone and update
// (see the JobForm "Configure Job" step / ApplicationQuestionsEditor). The
// array order IS the display order — the server numbers the rows from the
// position in this list and takes no order from the client. `id` is sent only
// for a question that already exists (edit) so the server updates it in place;
// create and clone send none, so every question is inserted fresh — which is
// what keeps a clone's questions independent of the source job.
//
// Always returns the key (even for an empty list) so an edit that removed the
// last question clears them; omits it entirely when the form never touched
// questions, which is how an update leaves an existing set alone.
const buildQuestionsPayload = (formData) => {
  const list = formData.applicationQuestions;
  if (!Array.isArray(list)) return {};
  return {
    application_questions: list.map((q) => ({
      ...(q.id ? { id: q.id } : {}),
      question: (q.question || "").trim(),
      answer_type: q.answerType,
      is_required: !!q.isRequired,
    })),
  };
};

const JOB_BASE = "/api/v2/job";
const JOB_ASSIGNMENT_BASE = "/api/v2/job";
const FILE_BASE = "/api/v2/file";
const AI_TOOL = "/api/v2/ai-tools";
const BOOLEAN_SEARCH_BASE = `${AI_TOOL}/boolean-search`;

export const JobService = {
  // ─── USER ENDPOINTS (public, no auth) ────

  fetJobList: async ({ page = 1, perPage = 10, search = "" }) => {
    try {
      const response = await apiService.get(`${JOB_BASE}/user-job-list`, {
        params: {
          page,
          per_page: perPage,
          search,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error while fetching job list:", error);
      throw error;
    }
  },

  fetJobDetails: async (jobId) => {
    try {
      const response = await apiService.get(
        `${JOB_BASE}/user-job-detail/${jobId}`
      );

      return response.data;
    } catch (error) {
      console.error(`Error while fetching job detail for ${jobId}:`, error);
      throw error;
    }
  },

  // Public version used by candidate-facing flows such as /user/apply/:jobId.
  // Must not trigger the authenticated 401 -> refresh -> login flow.
  fetchPublicJobFieldConfig: async (jobId) => {
    try {
      const response = await apiService.get(`${JOB_BASE}/field-config`, {
        params: { job_id: jobId },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching public job field config:", error);
      throw error;
    }
  },

  // ─── ADMIN ENDPOINTS (requires auth) ──────

  fetchAdminJobDetails: async ({
    page = 1,
    perPage = 10,
    pagination = true,
    status,
    search,
    department,
    division,
    location,
    employee_type,
    deadline_from,
    deadline_to,
    vacancy,
    hired,
    remaining,
    applicants,
    jobId,
    job_priority,
    top_job,
    calendar_type_id,
    ...rest
  }) => {
    try {
      const params = {
        page,
        per_page: perPage,
        search,
        pagination,
        ...(status && { status }),
        ...(department && { department }),
        ...(division && { division }),
        ...(location && { location }),
        ...(employee_type && { employee_type }),
        ...(deadline_from && { deadline_from: new Date(deadline_from).toISOString() }),
        ...(deadline_to && { deadline_to: new Date(deadline_to).toISOString() }),
        ...(vacancy && { vacancy }),
        ...(hired && { hired }),
        ...(remaining && { remaining }),
        ...(applicants && { applicants }),
        ...(jobId && { job_id: jobId }),
        ...(job_priority && { job_priority }),
        ...(top_job && { top_job }),
        ...(calendar_type_id && { calendar_type_id }),
        ...rest,
      };
      const response = await apiService.getSecure(`${JOB_BASE}/admin-job-list`, { params });
      return response.data;
    } catch (error) {
      console.error("Error while fetching job list for admin:", error);
      throw error;
    }
  },

  fetchMyJobDetails: async ({
    page = 1,
    perPage = 10,
    status,
    search,
    pagination = true,
    department,
    division,
    location,
    employee_type,
    deadline_from,
    deadline_to,
    vacancy,
    hired,
    remaining,
    applicants,
    jobId,
    job_priority,
    top_job,
  }) => {
    try {
      const params = {
        page,
        per_page: perPage,
        pagination,
        search,
        ...(status && { status }),
        ...(department && { department }),
        ...(division && { division }),
        ...(location && { location }),
        ...(employee_type && { employee_type }),
        ...(deadline_from && { deadline_from: new Date(deadline_from).toISOString() }),
        ...(deadline_to && { deadline_to: new Date(deadline_to).toISOString() }),
        ...(vacancy && { vacancy }),
        ...(hired && { hired }),
        ...(remaining && { remaining }),
        ...(applicants && { applicants }),
        ...(jobId && { job_id: jobId }),
        ...(job_priority && { job_priority }),
        ...(top_job && { top_job }),
      };
      const response = await apiService.getSecure(`${JOB_BASE}/admin-job-list`, { params });
      // const response = await apiService.getSecure(`${JOB_ASSIGNMENT_BASE}/my`, { params });
      return response.data;
    } catch (error) {
      console.error("Error while fetching job list for admin:", error);
      throw error;
    }
  },

  uploadJD: async (file, onUploadProgress) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiService.postSecure(`${FILE_BASE}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading JD:", error);
      throw error;
    }
  },

  manualJobPost: async (formData, skills) => {
    try {
      const payload = {
        title: formData.jobTitle || "",
        role_overview: formData.overview || "",
        key_responsibilities: formData.responsibility || "",
        assignedUserIds: formData.assignedTo ? [formData.assignedTo] : null,
        skills: skills,
        deadline: formatDateWithUserTimeZone(formData.deadline) || "",
        location: formData.location || "",
        ...buildShiftPayload(formData),
        ...buildCalendarTypePayload(formData),
        ...buildEmailConsentPayload(formData),
        ...buildQuestionsPayload(formData),
        vacancy_count: formData.vacancyCount || 0,
        experience_min: parseExperienceValue(formData.experienceMin),
        experience_max: parseExperienceValue(formData.experienceMax),
        employee_type: formData.employeeType || "",
        job_priority: formData.jobPriority || "",
        currency: formData.currency || "",
        currency_sign: formData.currency_sign || "",
        department: formData.department || "",
        division: formData.division || "",
        company_link: formData.companyWebsite || null,
        company_linkedin_link: formData.linkedIn || null,
        nice_to_have: formData.niceToHave
          ? formData.niceToHave
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
        certifications: formData.certifications
          ? formData.certifications
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
        languages_required: buildLanguagesRequiredPayload(formData),
        interviewers: (formData.interviewers || []).filter((i) => i.name || i.email),
        mandatory_fields: formData.mandatoryFields || [],
        configurable_fields: formData.selectedConfigurable || [],
        // `undefined`, not `[]`, when the form had no section to render —
        // JSON.stringify drops the key, and the server leaves the stored
        // column alone. `|| []` here would have said "select nothing".
        interview_question_types: formData.selectedInterviewTypes ?? undefined,
        boolean_platforms: formData.selectedBooleanPlatforms ?? undefined,
        // Omit to let the server default to the tenant's Default flow.
        status_flow_id: formData.statusFlowId || undefined,
      };

      const response = await apiService.postSecure(`${JOB_BASE}/create`, payload);
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || "Something went wrong";
      console.error("Error while posting job:", error);
      throw new Error(message);
    }
  },

  cloneJob: async (sourceJobId, formData) => {
    try {
      const skills = Array.isArray(formData.skills)
        ? formData.skills
        : (formData.skills || "").split(",").map((s) => s.trim()).filter(Boolean);

      const payload = {
        cloned_from_job_id: sourceJobId,
        title: formData.jobTitle || "",
        role_overview: formData.overview || "",
        key_responsibilities: formData.responsibility || "",
        assignedUserIds: formData.assignedTo ? [formData.assignedTo] : null,
        skills,
        deadline: formatDateWithUserTimeZone(formData.deadline) || "",
        location: formData.location || "",
        ...buildShiftPayload(formData),
        ...buildCalendarTypePayload(formData),
        ...buildEmailConsentPayload(formData),
        ...buildQuestionsPayload(formData),
        vacancy_count: formData.vacancyCount || 0,
        experience_min: parseExperienceValue(formData.experienceMin),
        experience_max: parseExperienceValue(formData.experienceMax),
        employee_type: formData.employeeType || "",
        job_priority: formData.jobPriority || "",
        currency: formData.currency || "",
        currency_sign: formData.currency_sign || "",
        department: formData.department || "",
        division: formData.division || "",
        company_link: formData.companyWebsite || null,
        company_linkedin_link: formData.linkedIn || null,
        nice_to_have: formData.niceToHave
          ? formData.niceToHave.split(",").map((c) => c.trim()).filter(Boolean)
          : [],
        certifications: formData.certifications
          ? formData.certifications.split(",").map((c) => c.trim()).filter(Boolean)
          : [],
        languages_required: buildLanguagesRequiredPayload(formData),
        interviewers: (formData.interviewers || []).filter((i) => i.name || i.email),
        mandatory_fields: formData.mandatoryFields || [],
        configurable_fields: formData.selectedConfigurable || [],
        // Same rule as create and update: `undefined` when the form had no
        // section to render, so the key is dropped and the server decides.
        // `|| []` would claim the user chose nothing.
        interview_question_types: formData.selectedInterviewTypes ?? undefined,
        boolean_platforms: formData.selectedBooleanPlatforms ?? undefined,
        // Omitting this lets the server inherit the source job's flow —
        // an explicit value here (the user changed the dropdown before
        // submitting the clone) still wins.
        status_flow_id: formData.statusFlowId || undefined,
      };

      const response = await apiService.postSecure(`${JOB_BASE}/create`, payload);
      return response.data;
    } catch (error) {
      // Structured 409 so callers can render a link to the duplicate job
      if (error?.response?.status === 409) {
        const apiError = new Error(error.response.data?.message || "An identical job already exists.");
        apiError.isDuplicate = true;
        apiError.duplicateData = error.response.data?.data ?? {};
        throw apiError;
      }
      console.error("Error while cloning job:", error);
      throw error;
    }
  },

  // ✅ Fix #2: Removed the broken duplicate `fetchJobById` that called
  //    `fetchAdminJobDetails` as an undefined local function. Keeping only
  //    the correct version below that hits the proper detail endpoint.
  fetchJobById: async (jobId) => {
    try {
      const response = await apiService.getSecure(`${JOB_BASE}/admin-job-detail/${jobId}`);
      return response.data;
    } catch (error) {
      console.error(`Error while fetching job detail for ${jobId}:`, error);
      throw error;
    }
  },

  fetchConfirmationText: async () => {
    try {
      const response = await apiService.getSecure(`${JOB_BASE}/confirmation-text`);
      return response.data;
    } catch (error) {
      console.error("Error fetching confirmation text:", error);
      throw error;
    }
  },

  updateJob: async (jobId, formData) => {
    try {
      const payload = {
        title: formData.jobTitle || "",
        deadline: formData.deadline || "",
        location: formData.location || "",
        employee_type: formData.employeeType || "",
        ...buildShiftPayload(formData),
        ...buildCalendarTypePayload(formData),
        ...buildEmailConsentPayload(formData),
        ...buildQuestionsPayload(formData),
        vacancy_count: formData.vacancyCount || 0,
        experience_min: parseExperienceValue(formData.experienceMin),
        experience_max: parseExperienceValue(formData.experienceMax),
        role_overview: formData.overview || "",
        job_priority: formData.jobPriority || "",
        key_responsibilities: formData.responsibility || "",
        acknowledged: formData.acknowledged || false,
        assignedUserIds: formData.assignedTo ? [formData.assignedTo] : null,
        nice_to_have: formData.niceToHave
          ? formData.niceToHave
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
        department: formData.department || "",
        division: formData.division || "",
        company_link: formData.companyWebsite || null,
        company_linkedin_link: formData.linkedIn || null,
        certifications: formData.certifications
          ? formData.certifications
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
        languages_required: buildLanguagesRequiredPayload(formData),
        interviewers: (formData.interviewers || []).filter((i) => i.name || i.email),
        skills: formData.skills || [],
        mandatory_fields: formData.mandatoryFields || [],
        configurable_fields: formData.selectedConfigurable || [],
        // Same rule as create: an absent key preserves, `[]` clears. This is
        // the path that used to wipe a withheld tenant's stored selection on
        // every edit.
        interview_question_types: formData.selectedInterviewTypes ?? undefined,
        boolean_platforms: formData.selectedBooleanPlatforms ?? undefined,
      };

      // Currency pair is atomic on the backend (both-or-neither):
      // only send it when BOTH values are present, otherwise omit the pair
      // so the backend leaves the job's currency untouched and no 422 fires.
      if (formData.currency && formData.currency_sign) {
        payload.currency = formData.currency;
        payload.currency_sign = formData.currency_sign;
      }

      // status_flow_id is always sent (the form's selector always shows the
      // job's current flow, unlike currency's optional-until-touched fields).
      // status_flow_acknowledged is a separate flag from `acknowledged`
      // above — one save could change both currency and flow, and
      // conflating the two would let confirming one silently authorize
      // the other.
      if (formData.statusFlowId) {
        payload.status_flow_id = formData.statusFlowId;
      }
      payload.status_flow_acknowledged = formData.statusFlowAcknowledged || false;

      const response = await apiService.putSecure(`${JOB_BASE}/update/${jobId}`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error while updating job ${jobId}:`, error);
      throw error;
    }
  },

  jdImproverStream: async (payload, onChunk) => {
    try {
      let processedLength = 0;
      // Persistent buffer so SSE "data:" lines that straddle a chunk boundary
      // are not dropped. axios delivers cumulative responseText on each
      // onDownloadProgress; we only consume the newly appended bytes.
      let buffer = "";

      // Process complete (newline-terminated) lines from the buffer. The final
      // element after split is a possibly-incomplete fragment: keep it in the
      // buffer unless `flush` is true (called once after the response ends).
      const drainBuffer = (flush) => {
        const parts = buffer.split("\n");
        buffer = flush ? "" : parts.pop();
        for (const line of parts) {
          const trimmed = line.trimEnd(); // tolerate CRLF
          if (!trimmed.startsWith("data: ")) continue;
          const json = trimmed.slice(6);
          if (!json) continue;
          try {
            onChunk(JSON.parse(json));
          } catch {
            // Malformed/partial JSON — skip this line.
          }
        }
      };

      await apiService.postSecure(`${AI_TOOL}/jd-improver/stream`, payload, {
        // This stream can remain open while the AI emits chunks, unlike normal
        // request/response calls that use the application's default timeout.
        timeout: 0,
        responseType: "text",
        onDownloadProgress: (progressEvent) => {
          const raw = progressEvent.event.target.responseText;
          if (raw.length <= processedLength) return;
          buffer += raw.slice(processedLength);
          processedLength = raw.length;
          drainBuffer(false);
        },
      });

      // Flush any trailing event the server sent without a final newline.
      if (buffer.length) drainBuffer(true);
    } catch (error) {
      console.error("JD Improver error:", error);
      throw error;
    }
  },

  // Open Job
  // openJob: async (jobId) => {
  //   try {
  //     const response = await apiService.patch(`${JOB_BASE}/status-change/${jobId}`);
  //     return response.data;
  //   } catch (error) {
  //     console.error(`Error opening job ${jobId}:`, error);
  //     throw error;
  //   }
  // },

  // Close Job
  // closeJob: async (jobId) => {
  //   try {
  //     const response = await apiService.patch(`${ADMIN_JOB_BASE}/job_close/${jobId}`);
  //     return response.data;
  //   } catch (error) {
  //     console.error(`Error closing job ${jobId}:`, error);
  //     throw error;
  //   }
  // },

  // Field Config
  fetchFieldConfig: async () => {
    try {
      const response = await apiService.getSecure(`${JOB_BASE}/field-config`);
      return response.data;
    } catch (error) {
      console.error("Error fetching field config:", error);
      throw error;
    }
  },

  fetchJobFieldConfig: async (jobId) => {
    try {
      const response = await apiService.getSecure(`${JOB_BASE}/field-config`, {
        params: { job_id: jobId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching job field config:", error);
      throw error;
    }
  },

  //  Interview Question Types

  /**
   * Fetch active interview question types.
   * @param {string} [jobId] - Optional. If provided, returns types specific to that job.
   */
  fetchInterviewQuestionTypes: async (jobId) => {
    try {
      const response = await apiService.getSecure(`${AI_TOOL}/interview-question-types/active`, jobId ? { params: { job_id: jobId } } : {});
      return response.data;
    } catch (error) {
      console.error("Error fetching interview question types:", error);
      throw error;
    }
  },

  // Boolean Search Strings
  fetchBooleanStrings: async (jobId) => {
    try {
      const response = await apiService.getSecure(`${BOOLEAN_SEARCH_BASE}/${jobId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching boolean strings for job ${jobId}:`, error);
      throw error;
    }
  },

  generateBooleanStrings: async (jobId, platforms) => {
    try {
      const response = await apiService.postSecure(`${BOOLEAN_SEARCH_BASE}/${jobId}/generate`, {
        platforms: platforms && platforms.length ? platforms : null,
      });
      return response.data;
    } catch (error) {
      console.error(`Error generating boolean strings for job ${jobId}:`, error);
      throw error;
    }
  },

  // Status Change
  jobStatusChange: async (jobId, status) => {
    try {
      const response = await apiService.patch(`${JOB_BASE}/status-change/${jobId}?status_value=${status}`);
      return response.data;
    } catch (error) {
      console.error(`Error closing job ${jobId}:`, error);
      throw error;
    }
  },
};
